import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AppointmentDetail,
  AppointmentListItem,
  AvailabilitySlot,
  Paginated,
  ScheduleBlockItem,
  WaitlistItem,
} from '@barbeiropro/contracts';
import { api } from '@/lib/api';

export type { AppointmentDetail, AppointmentListItem, AvailabilitySlot, ScheduleBlockItem, WaitlistItem };

export interface HourRow {
  id: string;
  weekday: number;
  opensAt: string;
  closesAt: string;
  barberId: string | null;
}

export interface Holiday {
  id: string;
  branchId: string | null;
  date: string;
  name: string;
}

export interface ServiceOption {
  id: string;
  name: string;
  durationMinutes: number;
  priceCents: number;
  active: boolean;
}

export interface CustomerOption {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
}

export const VISIBLE_STATUSES = ['requested', 'confirmed', 'in_progress', 'completed', 'no_show'];

export function useCalendar(p: { from: string; to: string; branchId?: string; barberId?: string }) {
  return useQuery({
    queryKey: ['agenda', 'calendar', p],
    queryFn: () => api.get<AppointmentListItem[]>('/appointments/calendar', { ...p, status: VISIBLE_STATUSES }),
    placeholderData: (prev) => prev,
    refetchInterval: 60_000,
  });
}

export function useBlocks(p: { from: string; to: string; branchId?: string }) {
  return useQuery({
    queryKey: ['agenda', 'blocks', p],
    queryFn: () => api.get<ScheduleBlockItem[]>('/schedule-blocks', p),
    placeholderData: (prev) => prev,
  });
}

export function useWaitlist(date: string, branchId?: string) {
  return useQuery({
    queryKey: ['agenda', 'waitlist', date, branchId ?? null],
    queryFn: () => api.get<WaitlistItem[]>('/waitlist', { date, branchId }),
  });
}

export function useBranchHours(branchId?: string) {
  return useQuery({
    queryKey: ['org', 'hours', branchId],
    queryFn: () => api.get<HourRow[]>('/org/hours', { branchId }),
    enabled: Boolean(branchId),
    staleTime: 300_000,
  });
}

export function useHolidays() {
  return useQuery({ queryKey: ['org', 'holidays'], queryFn: () => api.get<Holiday[]>('/org/holidays'), staleTime: 300_000 });
}

export function useServiceOptions() {
  return useQuery({
    queryKey: ['services', 'options'],
    queryFn: async () => (await api.get<Paginated<ServiceOption>>('/services', { pageSize: 100, active: 'true' })).data,
    staleTime: 60_000,
  });
}

export function useAvailability(p: { serviceId?: string; barberId?: string; branchId?: string; date?: string; excludeAppointmentId?: string }) {
  return useQuery({
    queryKey: ['agenda', 'availability', p],
    queryFn: () => api.get<AvailabilitySlot[]>('/appointments/availability', p),
    enabled: Boolean(p.serviceId && p.date),
    staleTime: 0,
  });
}

export function useAppointment(id: string | null) {
  return useQuery({
    queryKey: ['agenda', 'appointment', id],
    queryFn: () => api.get<AppointmentDetail>(`/appointments/${id}`),
    enabled: Boolean(id),
  });
}

export function useInvalidateAgenda() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['agenda'] });
}

export function useStatusMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { status: string; reason?: string }) => api.post<AppointmentDetail>(`/appointments/${id}/status`, body),
    onSuccess: (data) => {
      qc.setQueryData(['agenda', 'appointment', id], data);
      qc.invalidateQueries({ queryKey: ['agenda'] });
    },
  });
}

export function emailNote(status: AppointmentDetail['emailStatus']) {
  if (status === 'skipped_unconfigured') return 'E-mail ao cliente não enviado: o envio de e-mails não está disponível no momento.';
  if (status === 'queued') return 'E-mail ao cliente enfileirado para envio.';
  return null;
}
